import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus-black.svg';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import {
  Alert,
  Button,
  Flex,
  FlexColumn,
  IconButton,
  Link,
  PasswordField,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import { Typography } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import useSupportChat from 'shared/hooks/useSupportChat';
import { FetchIncidentIoApi, IncidentIoIntegrationForm } from 'shared/types';
import { IntegrationError } from 'settings/types';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import TestIntegrationSection from '../IntegrationTestSections/TestIntegrationSection';
import IncidentIoTestSection from '../IntegrationTestSections/IncidentIoTestSection';

export const INCIDENT_IO_TOKEN_PLACEHOLDER = 'xxxxxxxxxxxxxxxxxxxxxx';

type Props = {
  open: boolean;
  onClose: () => void;
  data?: FetchIncidentIoApi;
  onIncidentIoIntegrationSubmit: (data: IncidentIoIntegrationForm) => void;
  error?: IntegrationError;
  loading?: boolean;
  onDisconnect?: () => void;
};

const IncidentIoDialog = ({
  open,
  onClose,
  data,
  onIncidentIoIntegrationSubmit,
  error,
  loading,
  onDisconnect,
}: Props) => {
  const [showError, setShowError] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const supportChat = useSupportChat();

  const defaultValues = useMemo(
    (): IncidentIoIntegrationForm => ({
      sources:
        data?.sources && data.sources.length > 0
          ? data.sources.map((s) => ({
              name: s.name,
              alertSourceConfigId: s.alertSourceConfigId,
              token: INCIDENT_IO_TOKEN_PLACEHOLDER,
            }))
          : [{ name: '', alertSourceConfigId: '', token: '' }],
    }),
    [data],
  );

  const methods = useForm<IncidentIoIntegrationForm>({
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sources',
  });

  useEffect(() => {
    if (error) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [error]);

  const resetForm = useCallback(() => {
    setShowError(false);
    reset(defaultValues);
  }, [defaultValues, reset]);

  const missingSources = useMemo<string[]>(
    () =>
      (
        error as {
          response?: { data?: { extra?: { missing_sources?: string[] } } };
        }
      )?.response?.data?.extra?.missing_sources || [],
    [error],
  );

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect incident.io"
      onSubmit={handleSubmit((formData) => {
        const processedSources = formData.sources.map((source) => ({
          ...source,
          token:
            source.token === INCIDENT_IO_TOKEN_PLACEHOLDER ? '' : source.token,
        }));
        onIncidentIoIntegrationSubmit({ sources: processedSources });
      })}
      reset={resetForm}
      error={error}
      loading={loading}
      width={972}
    >
      <FlexColumn gap={3}>
        <Typography sx={{ fontSize: '12px', color: 'common.gray2' }}>
          Configure alert sources from your{' '}
          <Link
            href="https://docs.incident.io/alerts/custom-http-sources"
            target="_blank"
            rel="noopener noreferrer"
          >
            incident.io HTTP alert sources
          </Link>
          . Each source has its own name, config ID, and token.
        </Typography>

        <FlexColumn sx={{ gap: 1 }}>
          {fields.map((field, index) => (
            <Flex gap={1} alignItems="center" key={field.id}>
              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="source name"
                  {...register(`sources.${index}.name`, {
                    required: requiredLabel,
                  })}
                  size="large"
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.sources?.[index]?.name?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="alert source config ID"
                  {...register(`sources.${index}.alertSourceConfigId`, {
                    required: requiredLabel,
                    onChange: (e) => {
                      const value = e.target.value;
                      const match = value.match(
                        /\/alert_events\/http\/([A-Za-z0-9]+)/,
                      );
                      if (match) {
                        setValue(
                          `sources.${index}.alertSourceConfigId`,
                          match[1],
                        );
                      }
                    },
                  })}
                  size="large"
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.sources?.[index]?.alertSourceConfigId?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <PasswordField
                  fullWidth
                  placeholder="API token"
                  size="large"
                  {...register(`sources.${index}.token`, {
                    required: requiredLabel,
                    onChange: (e) => {
                      const value = e.target.value.trim();
                      const bearerMatch = value.match(
                        /Bearer\s+([A-Za-z0-9_\-./]+)/,
                      );
                      if (bearerMatch) {
                        setValue(`sources.${index}.token`, bearerMatch[1]);
                      }
                    },
                  })}
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.sources?.[index]?.token?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <IconButton onClick={() => remove(index)}>
                <TrashIcon />
              </IconButton>
            </Flex>
          ))}

          <Button
            startIcon={<PlusIcon />}
            onClick={() =>
              append({ name: '', alertSourceConfigId: '', token: '' })
            }
            sx={{ width: '160px' }}
            variant="text"
            color="secondary"
          >
            <Typography fontSize={14}>Add source</Typography>
          </Button>
        </FlexColumn>

        {missingSources.length > 0 && (
          <Alert severity="error">
            The following sources are configured in alert rules, but are missing
            from the configuration
            {missingSources.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </Alert>
        )}

        {showError && missingSources.length === 0 && (
          <Alert severity="error" sx={{ fontWeight: 'bold' }}>
            Elementary incident.io app failed to connect, please{' '}
            <Link onClick={supportChat.show} href="#">
              contact us
            </Link>{' '}
            for further assistance.
          </Alert>
        )}

        <TestIntegrationSection isSuccess={testSuccess}>
          <IncidentIoTestSection
            sources={watch('sources')}
            onSuccess={() => setTestSuccess(true)}
            onReset={() => setTestSuccess(false)}
          />
        </TestIntegrationSection>

        {data?.integrated && onDisconnect && (
          <Button
            variant="text"
            color="error"
            onClick={onDisconnect}
            sx={{ alignSelf: 'flex-start', mt: 1 }}
          >
            Disconnect incident.io
          </Button>
        )}
      </FlexColumn>
    </IntegrationDialog>
  );
};

export default IncidentIoDialog;
